// ─────────────────────────────────────────────────────────────────────────
// Firestore access layer for the CRM module.
//
// Collections used (new — do not clash with anything already in the app):
//   crmLeads     – pipeline / deal cards
//   crmContacts  – people at a lead's society
//   crmTasks     – follow-ups, optionally linked to a lead/contact
//   crmPnl       – manual business-wide income/expense entries
//   crmPnlEntries  – rebuilt P&L entries (new storage; old crmPnl is left untouched)
//   crmPnlSettings – P&L project list and Google Cloud project links (one document: "main")
//
// Visibility rule (per product decision): a "Sales" rep only sees records
// assigned to them; "SalesHead" and everyone else with dashboard access
// sees everything. `canSeeAll(user)` centralizes that check.
// ─────────────────────────────────────────────────────────────────────────
import {
  collection, addDoc, updateDoc, deleteDoc, doc, getDoc, setDoc,
  getDocs, query, orderBy, where, serverTimestamp, writeBatch,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { mapOldEntry, missingOld, NEW_ID_PREFIX } from "./pnlMigrate";
import { fmtDT } from "./dateFormat";

export const canSeeAll = (user) => user?.role !== "Sales";

// Keep only records assigned to this user, unless they're allowed to see all.
export function visibleTo(records, user) {
  if (canSeeAll(user)) return records;
  return records.filter((r) => r.assignedToUid === user?.uid);
}

// ── Leads ──────────────────────────────────────────────────────────────
export async function fetchLeads() {
  const snap = await getDocs(query(collection(db, "crmLeads"), orderBy("createdAt", "desc")));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function createLead(data, user) {
  const leadRef = await addDoc(collection(db, "crmLeads"), {
    ...data,
    createdByUid: user.uid,
    createdByName: user.name || user.email,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  // Every new lead automatically gets a follow-up task, so it never
  // just sits silently in "New Lead" unnoticed.
  const dueDate = new Date();
  dueDate.setDate(dueDate.getDate() + 1);
  await addDoc(collection(db, "crmTasks"), {
    title: `Contact ${data.prospectName || data.societyName}`,
    leadId: leadRef.id,
    dueDate: dueDate.toISOString().slice(0, 10),
    priority: "High",
    notes: "",
    status: "pending",
    assignedToUid: data.assignedToUid || "",
    assignedToName: data.assignedToName || "",
    createdByUid: user.uid,
    createdByName: user.name || user.email,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return leadRef;
}

export async function updateLead(id, data) {
  return updateDoc(doc(db, "crmLeads", id), { ...data, updatedAt: serverTimestamp() });
}

// Adds a timestamped note line, keeping earlier notes — used when a lead
// moves stage, so there's always a record of what happened and why.
export function appendLeadNote(existingNotes, noteText, user) {
  const stamp = fmtDT(new Date());
  const entry = `[${stamp}] ${user.name || user.email}: ${noteText}`;
  return existingNotes ? `${existingNotes}\n${entry}` : entry;
}

// When a lead moves forward, its still-open follow-up task(s) are done —
// mark them complete instead of leaving them pending forever.
export async function completeOpenTasksForLead(leadId) {
  const snap = await getDocs(
    query(collection(db, "crmTasks"), where("leadId", "==", leadId), where("status", "==", "pending"))
  );
  await Promise.all(
    snap.docs.map((d) => updateDoc(doc(db, "crmTasks", d.id), { status: "done", updatedAt: serverTimestamp() }))
  );
}

export async function deleteLead(id) {
  return deleteDoc(doc(db, "crmLeads", id));
}

// ── Contacts ───────────────────────────────────────────────────────────
export async function fetchContacts() {
  const snap = await getDocs(query(collection(db, "crmContacts"), orderBy("createdAt", "desc")));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function createContact(data, user) {
  return addDoc(collection(db, "crmContacts"), {
    ...data,
    interactions: [],
    createdByUid: user.uid,
    createdByName: user.name || user.email,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function updateContact(id, data) {
  return updateDoc(doc(db, "crmContacts", id), { ...data, updatedAt: serverTimestamp() });
}

export async function deleteContact(id) {
  return deleteDoc(doc(db, "crmContacts", id));
}

export async function addContactInteraction(contact, note, user) {
  const interactions = [
    ...(contact.interactions || []),
    { date: new Date().toISOString(), note, byName: user.name || user.email },
  ];
  await updateDoc(doc(db, "crmContacts", contact.id), { interactions, updatedAt: serverTimestamp() });
  return interactions;
}

// ── Tasks ──────────────────────────────────────────────────────────────
export async function fetchTasks() {
  const snap = await getDocs(query(collection(db, "crmTasks"), orderBy("dueDate", "asc")));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function createTask(data, user) {
  return addDoc(collection(db, "crmTasks"), {
    ...data,
    status: "pending",
    createdByUid: user.uid,
    createdByName: user.name || user.email,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function updateTask(id, data) {
  return updateDoc(doc(db, "crmTasks", id), { ...data, updatedAt: serverTimestamp() });
}

export async function deleteTask(id) {
  return deleteDoc(doc(db, "crmTasks", id));
}

// ── Business-wide P&L entries ─────────────────────────────────────────
export async function fetchPnlEntries() {
  const snap = await getDocs(query(collection(db, "crmPnl"), orderBy("date", "desc")));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function createPnlEntry(data, user) {
  return addDoc(collection(db, "crmPnl"), {
    ...data,
    createdByUid: user.uid,
    createdByName: user.name || user.email,
    createdAt: serverTimestamp(),
  });
}

export async function deletePnlEntry(id) {
  return deleteDoc(doc(db, "crmPnl", id));
}

// ── P&L settings: project list and Google Cloud links (Owners only) ──────
// "Company (common)" is always there and can never be hidden or removed.
export const COMMON_PROJECT = "Company (common)";
export const DEFAULT_PNL_SETTINGS = {
  projects: [
    { name: "Main app", hidden: false },
    { name: "CRM", hidden: false },
    { name: "Billing app", hidden: false },
    { name: "Website", hidden: false },
    { name: COMMON_PROJECT, hidden: false },
  ],
  googleLinks: [
    { googleProject: "abc-saas-test", app: "Main app" },
    { googleProject: "abc-saas-prod", app: "Main app" },
    { googleProject: "abc-invoice-system", app: "Billing app" },
    { googleProject: "abc-dashboard001", app: "CRM" },
  ],
};

export async function fetchPnlSettings() {
  const snap = await getDoc(doc(db, "crmPnlSettings", "main"));
  if (!snap.exists()) return { ...DEFAULT_PNL_SETTINGS, saved: false };
  const d = snap.data();
  return {
    projects: Array.isArray(d.projects) && d.projects.length ? d.projects : DEFAULT_PNL_SETTINGS.projects,
    googleLinks: Array.isArray(d.googleLinks) ? d.googleLinks : DEFAULT_PNL_SETTINGS.googleLinks,
    saved: true,
  };
}

export async function savePnlSettings(settings, user) {
  return setDoc(doc(db, "crmPnlSettings", "main"), {
    projects: settings.projects,
    googleLinks: settings.googleLinks,
    updatedAt: serverTimestamp(),
    updatedByUid: user.uid,
    updatedByName: user.name || user.email,
  });
}

// ── Rebuilt P&L entries (new storage, Owners only) ───────────────────────
export async function fetchPnlEntriesV2() {
  const snap = await getDocs(query(collection(db, "crmPnlEntries"), orderBy("date", "desc")));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// One-time copy of the old entries. Safe to press again: entries already copied are skipped.
// The old entries are only read, never changed. Returns how many were copied now.
export async function copyOldPnlEntries(user) {
  const [oldList, newList] = await Promise.all([fetchPnlEntries(), fetchPnlEntriesV2()]);
  const todo = missingOld(oldList, newList);
  for (let i = 0; i < todo.length; i += 400) {
    const batch = writeBatch(db);
    todo.slice(i, i + 400).forEach((e) => {
      batch.set(doc(db, "crmPnlEntries", NEW_ID_PREFIX + e.id), {
        ...mapOldEntry(e, user, COMMON_PROJECT),
        migratedAt: serverTimestamp(),
      });
    });
    await batch.commit();
  }
  return todo.length;
}

// New entries in the rebuilt storage. Entries can be added and cancelled, never deleted. They can only be
// edited in four boxes (see updatePnlEntryV2), plus a one-time Paid-from tag on copied old expenses.
function newEntryFields(data, user) {
  return {
    ...data,
    createdByUid: user.uid,
    createdByName: user.name || user.email,
    createdAt: serverTimestamp(),
  };
}

export async function createPnlEntryV2(data, user) {
  return addDoc(collection(db, "crmPnlEntries"), newEntryFields(data, user));
}

// Several entries at once (all saved together or none), e.g. a Google Cloud bill split by project.
export async function createPnlEntriesV2(list, user) {
  const batch = writeBatch(db);
  list.forEach((data) => batch.set(doc(collection(db, "crmPnlEntries")), newEntryFields(data, user)));
  await batch.commit();
  return list.length;
}

// Edit one entry (step 5). "update" comes from buildEdit in pnlEntry.js: only the changed boxes
// (description, customer / vendor, project, category) plus the longer change record (editLog).
// The database rule allows nothing else, so money, date, GST, TDS and Paid from cannot change.
export async function updatePnlEntryV2(id, update) {
  return updateDoc(doc(db, "crmPnlEntries", id), update);
}

// One-time: the old (copied) expenses were all paid from the Owner's pocket (Owner confirmed
// 17 Oct 2026). Only the Paid-from field is written; the database rule allows nothing else.
export async function tagOldExpensesOwner() {
  const all = await fetchPnlEntriesV2();
  const todo = all.filter((e) => e.source === "migrated" && e.type === "Expense" && !e.paidFrom);
  for (let i = 0; i < todo.length; i += 400) {
    const batch = writeBatch(db);
    todo.slice(i, i + 400).forEach((e) => batch.update(doc(db, "crmPnlEntries", e.id), { paidFrom: "Owner" }));
    await batch.commit();
  }
  return todo.length;
}

// ── Derived numbers used across screens ───────────────────────────────
// A lead only counts as real revenue once it's Won.
export function leadRevenue(lead) {
  return (Number(lead.subscriptionValue) || 0) + (Number(lead.onboardingFee) || 0);
}

export function pipelineValue(leads) {
  return leads
    .filter((l) => l.stage !== "Won" && l.stage !== "Lost")
    .reduce((sum, l) => sum + leadRevenue(l), 0);
}

export function wonRevenue(leads) {
  return leads.filter((l) => l.stage === "Won").reduce((sum, l) => sum + leadRevenue(l), 0);
}